<template>
  <div v-if="!hasPermission" class="m-3">
    <access-denied />
  </div>
  <div v-else class="p-3">

    <!-- Header: Title + Date Range + Print -->
    <div class="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-4 gap-2">
      <div>
        <small class="text-muted">{{ $t('Current Sales Summary') }} — {{ selectedBranchLabel }}</small>
      </div>
      <div class="d-flex align-items-center gap-2 flex-wrap d-print-none">
        <!-- Branch Selector -->
        <select v-model="selectedBranch" class="form-select form-select-sm" style="width: auto; min-width: 140px;" :disabled="loading">
          <option value="all">{{ $t('All Branches') }}</option>
          <option v-for="b in branches" :key="b.id" :value="b.id">{{ b.name }}</option>
        </select>
        <!-- Date Range -->
        <div class="input-group input-group-sm w-auto date-range-input-group">
          <span class="input-group-text"><i class="fa-regular fa-calendar"></i></span>
          <date-range-picker
            v-model="filters.date_range"
            :date-range="filters.date_range"
            :locale-data="dateRangeLocale"
            :ranges="dateRanges"
            :opens="'left'"
            :auto-apply="true"
            :show-dropdowns="true"
            :control-container-class="''"
          >
            <template v-slot:input="picker">
              <span v-if="picker.startDate && picker.endDate">
                {{ formatDate(picker.startDate) }} — {{ formatDate(picker.endDate) }}
              </span>
              <span v-else class="text-muted">{{ $t('Select date range') }}</span>
            </template>
          </date-range-picker>
        </div>
        <!-- Print -->
        <!-- <button class="btn btn-sm btn-outline-secondary" @click="printReport" :title="$t('Print Report')" :disabled="loading">
          <i class="fa-solid fa-print"></i>
        </button> -->
      </div>
    </div>

    <!-- Print-only header -->
    <div class="d-none d-print-block mb-3 pb-2 border-bottom">
      <h4 class="fw-bold mb-1">{{ $t('Sales Report') }}</h4>
      <div class="d-flex justify-content-between small text-muted">
        <div>
          <strong>{{ $t('Branch') }}:</strong> {{ selectedBranchLabel }}
          &nbsp;|&nbsp;
          <strong>{{ $t('Period') }}:</strong> {{ rangeLabel }}
        </div>
        <div><strong>{{ $t('Printed at') }}:</strong> {{ printedAt }}</div>
      </div>
    </div>

    <!-- Error -->
    <div v-if="errorMessage" class="alert alert-danger d-flex justify-content-between align-items-center" role="alert">
      <span><i class="fa-solid fa-triangle-exclamation me-2"></i>{{ errorMessage }}</span>
      <button class="btn btn-sm btn-outline-danger" @click="fetchDashboard">{{ $t('Retry') }}</button>
    </div>

    <!-- Loading overlay wrapper -->
    <div :class="{ 'dashboard-loading': loading }">

    <!-- Summary Cards -->
    <div class="row g-3 mb-4">
      <div class="col-12 col-lg-3" v-for="card in summaryCards" :key="card.key">
        <div class="card border-0 shadow-sm h-100 dashboard-summary-card" @click="card.onClick?.()">
          <div class="card-body p-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="text-muted small">{{ $t(card.label) }}</div>
              <div class="dashboard-summary-icon" :class="card.iconBg">
                <i :class="card.icon"></i>
              </div>
            </div>
            <div class="fw-bold fs-4 dashboard-summary-value">{{ card.value }}</div>
            <div class="small mt-1" :class="card.trendUp ? 'text-success' : 'text-danger'">
              <i :class="card.trendUp ? 'fa-solid fa-arrow-up' : 'fa-solid fa-arrow-down'" class="me-1"></i>
              {{ card.trend }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row g-3 mb-4">
      <!-- Revenue Chart -->
      <div class="col-12 col-lg-8">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3">
            <h6 class="mb-0 fw-semibold">
              <i class="fa-solid fa-chart-bar me-2 text-primary"></i>{{ $t('Sales & Revenue') }}
            </h6>
            <small class="text-muted">{{ $t('Last 7 days') }}</small>
          </div>
          <div class="card-body pt-0">
            <apexchart
              type="bar"
              height="280"
              :options="revenueChartOptions"
              :series="revenueChartSeries"
            />
          </div>
        </div>
      </div>

      <!-- Payment Methods Donut -->
      <div class="col-12 col-lg-4">
        <div class="card border-0 shadow-sm h-100">
          <div class="card-header bg-white border-0 py-3">
            <h6 class="mb-0 fw-semibold">
              <i class="fa-solid fa-credit-card me-2 text-success"></i>{{ $t('Payment Methods') }}
            </h6>
          </div>
          <div class="card-body pt-0 d-flex flex-column">
            <apexchart
              type="donut"
              height="200"
              :options="paymentChartOptions"
              :series="paymentChartSeries"
            />
            <div class="mt-auto">
              <div v-for="(pm, i) in paymentMethodsDisplay" :key="i" class="d-flex justify-content-between small py-1 border-bottom">
                <span>
                  <i :class="pm.icon" class="me-2" :style="{ color: pm.color }"></i>{{ $t(pm.label) }}
                </span>
                <span class="fw-semibold">{{ formatCurrency(pm.amount) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom Row: Recent Orders + Top Products -->
    <div class="row g-3 mb-4">
      <!-- Recent Orders -->
      <div class="col-12 col-lg-7">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3">
            <h6 class="mb-0 fw-semibold">
              <i class="fa-solid fa-receipt me-2 text-warning"></i>{{ $t('Recent Orders') }}
            </h6>
            <button class="btn btn-sm btn-link text-decoration-none p-0" @click="goBill">
              {{ $t('View All') }} <i class="fa-solid fa-arrow-right ms-1"></i>
            </button>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0 align-middle">
                <thead class="table-light">
                  <tr>
                    <th class="ps-3">{{ $t('Receipt No.') }}</th>
                    <th>{{ $t('Table') }}</th>
                    <th>{{ $t('Items') }}</th>
                    <th class="text-end">{{ $t('Total') }}</th>
                    <th class="text-center">{{ $t('Status') }}</th>
                    <th class="text-center pe-3">{{ $t('Time') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!recentBills.length && !loading">
                    <td colspan="6" class="text-center text-muted py-4">{{ $t('No data') }}</td>
                  </tr>
                  <tr v-for="bill in recentBills" :key="bill.id" role="button" @click="viewBill(bill)">
                    <td class="ps-3 fw-medium">{{ bill.receipt_no || '-' }}</td>
                    <td>{{ bill.table_name || '-' }}</td>
                    <td>{{ bill.item_count }} {{ $t('items') }}</td>
                    <td class="text-end fw-semibold">{{ formatCurrency(bill.total) }}</td>
                    <td class="text-center">
                      <span class="badge rounded-pill" :class="paymentStatusClass(bill.payment_status)">
                        {{ $t(bill.payment_status) }}
                      </span>
                    </td>
                    <td class="text-center pe-3 text-muted small">{{ formatTime(bill.closed_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Selling Products -->
      <div class="col-12 col-lg-5">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3">
            <h6 class="mb-0 fw-semibold">
              <i class="fa-solid fa-ranking-star me-2 text-info"></i>{{ $t('Top Selling Products') }}
            </h6>
            <small class="text-muted">{{ rangeLabel }}</small>
          </div>
          <div class="card-body p-0">
            <div v-if="!topProducts.length && !loading" class="text-center text-muted py-4">{{ $t('No data') }}</div>
            <div v-for="(product, i) in topProducts" :key="product.product_id" class="d-flex align-items-center px-3 py-2 border-bottom">
              <div class="dashboard-rank-badge me-3" :class="i < 3 ? 'rank-top' : ''">{{ i + 1 }}</div>
              <div class="flex-grow-1">
                <div class="fw-medium">{{ $t(product.name) }}</div>
                <small class="text-muted">{{ $t(product.category) }}</small>
              </div>
              <div class="text-end">
                <div class="fw-semibold">{{ product.qty }} {{ $t('sold') }}</div>
                <small class="text-muted">{{ formatCurrency(product.revenue) }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hourly Orders Heatmap -->
    <div class="row g-3 mb-4">
      <div class="col-12">
        <div class="card border-0 shadow-sm">
          <div class="card-header bg-white border-0 d-flex justify-content-between align-items-center py-3">
            <h6 class="mb-0 fw-semibold">
              <i class="fa-solid fa-clock me-2 text-secondary"></i>{{ $t('Orders by Hour') }}
            </h6>
            <small class="text-muted">{{ $t('Today') }}</small>
          </div>
          <div class="card-body pt-0">
            <apexchart
              type="heatmap"
              height="160"
              :options="hourlyChartOptions"
              :series="hourlyChartSeries"
            />
          </div>
        </div>
      </div>
    </div>

    </div><!-- /loading wrapper -->

    <!-- Loading spinner -->
    <div v-if="loading" class="dashboard-loading-overlay">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ $t('Loading') + '...' }}</span>
      </div>
    </div>

  </div>
</template>

<script>
import moment from 'moment';
import VueApexCharts from 'vue3-apexcharts';
import DateRangePicker from 'vue3-daterange-picker';
import { Dashboard } from '@/services/endpoint/dashboard.service';
import AccessDenied from '@/plugins/AccessDenied.vue';

const PAYMENT_METHOD_META = {
  cash:      { icon: 'fa-solid fa-money-bill-wave', color: '#22c55e' },
  card:      { icon: 'fa-solid fa-credit-card',     color: '#3b82f6' },
  promptpay: { icon: 'fa-solid fa-qrcode',          color: '#a855f7' },
  delivery:  { icon: 'fa-solid fa-motorcycle',      color: '#f97316' },
};

const DAY_LABEL_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default {
  name: 'DashboardIndex',
  components: {
    apexchart: VueApexCharts,
    AccessDenied,
    DateRangePicker,
  },
  data() {
    const today = new Date();
    return {
      selectedBranch: 'all',
      loading: false,
      hasPermission: true,
      errorMessage: '',
      filters: {
        // default = today (matches the previous default range)
        date_range: { startDate: today, endDate: today },
      },

      printedAt: '',

      // ─── data from API ──────────────────────────────────────────────
      branches: [],
      summary: null,
      revenueChart: { categories: [], gross: [], net: [] },
      paymentMethods: [],
      recentBills: [],
      topProducts: [],
      hourlyOrders: [],
    };
  },
  computed: {
    selectedBranchLabel() {
      if (this.selectedBranch === 'all') return this.$t('All Branches');
      return this.branches.find(b => b.id === this.selectedBranch)?.name || '';
    },
    // Human-readable label for the selected range, e.g. "2026-07-07" (single
    // day) or "2026-07-01 — 2026-07-07". Shown on the print header + top products.
    rangeLabel() {
      const from = this.formatDate(this.filters.date_range.startDate);
      const to = this.formatDate(this.filters.date_range.endDate);
      return from === to ? from : `${from} — ${to}`;
    },

    // Quick-select presets for the date-range picker. Keys are translated
    // labels; values are [start, end] Date pairs the picker applies on click.
    dateRanges() {
      const endToday = moment().endOf('day').toDate();
      return {
        [this.$t('Today')]: [moment().startOf('day').toDate(), endToday],
        [this.$t('This week')]: [moment().startOf('isoWeek').toDate(), endToday],
        [this.$t('This month')]: [moment().startOf('month').toDate(), endToday],
        [this.$t('This year')]: [moment().startOf('year').toDate(), endToday],
        [this.$t('Last year')]: [
          moment().subtract(1, 'year').startOf('year').toDate(),
          moment().subtract(1, 'year').endOf('year').toDate(),
        ],
      };
    },
    dateRangeLocale() {
      return {
        firstDay: 1,
        format: 'yyyy-mm-dd',
        applyLabel: this.$t('Apply'),
        cancelLabel: this.$t('Cancel'),
        clearable: false,
        monthNames: this.$storeIndex.monthNames(),
        daysOfWeek: this.$storeIndex.daysOfWeek(),
      };
    },

    // ─── Summary Cards ────────────────────────────────────────────────
    summaryCards() {
      const s = this.summary || {};
      const gross  = s.gross_sales   || { value: 0, diff_percent: 0, trend_up: true };
      const net    = s.net_sales     || { value: 0, diff_percent: 0, trend_up: true };
      const orders = s.total_orders  || { value: 0, diff_count: 0,   trend_up: true };
      const avg    = s.avg_per_order || { value: 0, diff_percent: 0, trend_up: true };

      return [
        {
          key: 'gross', label: 'Gross Sales',
          value: this.formatCurrency(gross.value),
          trend: this.formatPercent(gross.diff_percent), trendUp: gross.trend_up,
          icon: 'fa-solid fa-coins', iconBg: 'icon-bg-primary',
        },
        {
          key: 'net', label: 'Net Sales',
          value: this.formatCurrency(net.value),
          trend: this.formatPercent(net.diff_percent), trendUp: net.trend_up,
          icon: 'fa-solid fa-wallet', iconBg: 'icon-bg-success',
        },
        {
          key: 'orders', label: 'Total Orders',
          value: String(orders.value),
          trend: this.formatOrdersDiff(orders.diff_count), trendUp: orders.trend_up,
          icon: 'fa-solid fa-bag-shopping', iconBg: 'icon-bg-warning',
          onClick: () => this.goBill(),
        },
        {
          key: 'avg', label: 'Avg. per Order',
          value: this.formatCurrency(avg.value),
          trend: this.formatPercent(avg.diff_percent), trendUp: avg.trend_up,
          icon: 'fa-solid fa-calculator', iconBg: 'icon-bg-info',
        },
      ];
    },

    // ─── Revenue Bar Chart ────────────────────────────────────────────
    revenueChartSeries() {
      return [
        { name: this.$t('Gross Sales'), data: this.revenueChart.gross || [] },
        { name: this.$t('Net Sales'),   data: this.revenueChart.net   || [] },
      ];
    },
    revenueChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        plotOptions: { bar: { borderRadius: 4, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: (this.revenueChart.categories || []).map(d => this.formatDayLabel(d)),
        },
        yaxis: {
          labels: { formatter: (v) => this.formatCompact(v) },
        },
        colors: ['#3b82f6', '#93c5fd'],
        legend: { position: 'top', horizontalAlign: 'right' },
        tooltip: {
          y: { formatter: (v) => this.formatCurrency(v) },
        },
        grid: { borderColor: '#f1f5f9' },
      };
    },

    // ─── Payment Donut Chart ──────────────────────────────────────────
    paymentMethodsDisplay() {
      return this.paymentMethods.map(pm => ({
        ...pm,
        icon:  PAYMENT_METHOD_META[pm.code]?.icon  || 'fa-solid fa-circle',
        color: PAYMENT_METHOD_META[pm.code]?.color || '#94a3b8',
      }));
    },
    paymentChartSeries() {
      return this.paymentMethodsDisplay.map(pm => Number(pm.amount) || 0);
    },
    paymentChartOptions() {
      const formatCurrency = this.formatCurrency;
      return {
        chart: { fontFamily: 'Sarabun, sans-serif' },
        labels: this.paymentMethodsDisplay.map(pm => this.$t(pm.label)),
        colors: this.paymentMethodsDisplay.map(pm => pm.color),
        legend: { show: false },
        dataLabels: { enabled: false },
        plotOptions: {
          pie: {
            donut: {
              size: '70%',
              labels: {
                show: true,
                value: {
                  formatter: (v) => formatCurrency(Number(v) || 0),
                },
                total: {
                  show: true,
                  showAlways: true,
                  label: this.$t('Total'),
                  formatter: (w) => {
                    const sum = (w?.globals?.seriesTotals || []).reduce((a, b) => Number(a) + Number(b || 0), 0);
                    return formatCurrency(sum);
                  },
                },
              },
            },
          },
        },
        tooltip: {
          y: { formatter: (v) => formatCurrency(Number(v) || 0) },
        },
      };
    },

    // ─── Hourly Heatmap ──────────────────────────────────────────────
    hourlyChartSeries() {
      // Map API counts by hour so we can fill a fixed 0–23 axis.
      const countByHour = {};
      (this.hourlyOrders || []).forEach(h => {
        countByHour[Number(h.hour)] = Number(h.count) || 0;
      });
      const data = Array.from({ length: 24 }, (_, hour) => ({
        x: `${String(hour).padStart(2, '0')}:00`,
        y: countByHour[hour] || 0,
      }));
      return [{ name: this.$t('Orders'), data }];
    },
    hourlyChartOptions() {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'Sarabun, sans-serif' },
        dataLabels: { enabled: true, style: { fontSize: '11px' } },
        colors: ['#3b82f6'],
        plotOptions: {
          heatmap: {
            radius: 4,
            colorScale: {
              ranges: [
                { from: 0,  to: 0,  color: '#f1f5f9', name: 'none' },
                { from: 1,  to: 5,  color: '#bfdbfe', name: 'low' },
                { from: 6,  to: 12, color: '#60a5fa', name: 'medium' },
                { from: 13, to: 20, color: '#3b82f6', name: 'high' },
                { from: 21, to: 999, color: '#1d4ed8', name: 'peak' },
              ],
            },
          },
        },
        xaxis: {
          labels: { rotate: -45, style: { fontSize: '10px' } },
        },
        tooltip: {
          enabled: true,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            const hour = w.globals.labels[dataPointIndex];
            const count = series[seriesIndex][dataPointIndex] || 0;
            return (
              '<div class="px-2 py-1">' +
                `<div class="fw-semibold">${hour}</div>` +
                `<div>${count} ${this.$t('orders')}</div>` +
              '</div>'
            );
          },
        },
      };
    },
  },
  watch: {
    selectedBranch() { this.fetchDashboard(); },
    'filters.date_range': {
      deep: true,
      handler(val, old) {
        const a = val?.startDate ? String(val.startDate) : '';
        const b = val?.endDate ? String(val.endDate) : '';
        const oa = old?.startDate ? String(old.startDate) : '';
        const ob = old?.endDate ? String(old.endDate) : '';
        if (a === oa && b === ob) return;
        this.fetchDashboard();
      },
    },
  },
  mounted() {
    this.fetchDashboard();
  },
  methods: {
    async fetchDashboard() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const res = await Dashboard.get({
          branch_id: this.selectedBranch,
          start: this.formatDate(this.filters.date_range.startDate),
          end: this.formatDate(this.filters.date_range.endDate),
        });
        const payload = res?.data?.data || {};
        const filter  = res?.data?.filter || {};

        this.branches       = filter.branches      || [];
        this.summary        = payload.summary      || null;
        this.revenueChart   = payload.revenue_chart || { categories: [], gross: [], net: [] };
        this.paymentMethods = payload.payment_methods || [];
        this.recentBills    = payload.recent_bills    || [];
        this.topProducts    = payload.top_products    || [];
        this.hourlyOrders   = payload.hourly_orders   || [];
      } catch (err) {
        // ไม่มีสิทธิ์ดู dashboard (BE gate can:dashboard_view) → แสดง AccessDenied แทนทั้งหน้า
        if (err?.response?.status === 403) {
          this.hasPermission = false;
        } else {
          this.errorMessage = this.$t(err?.response?.data?.message) || err?.message || this.$t('Failed to load data');
        }
      } finally {
        this.loading = false;
      }
    },

    formatDate(d) {
      return d ? moment(d).format('YYYY-MM-DD') : '';
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('th-TH', {
        style: 'currency', currency: 'THB', minimumFractionDigits: 0,
      }).format(value || 0);
    },
    formatCompact(value) {
      if (value >= 1000) return `${(value / 1000).toFixed(0)}k`;
      return String(value);
    },
    formatPercent(value) {
      const v = Number(value || 0);
      const sign = v >= 0 ? '+' : '';
      return `${sign}${v.toFixed(1)}%`;
    },
    formatOrdersDiff(value) {
      const v = Number(value || 0);
      const sign = v >= 0 ? '+' : '';
      return `${sign}${v} ${this.$t('orders')}`;
    },
    formatDayLabel(isoDate) {
      if (!isoDate) return '';
      const d = new Date(isoDate);
      if (isNaN(d.getTime())) return isoDate;
      return this.$t(DAY_LABEL_KEYS[d.getDay()]);
    },
    formatTime(isoDateTime) {
      if (!isoDateTime) return '';
      const d = new Date(isoDateTime);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    },
    paymentStatusClass(status) {
      let bg = 'bg-secondary';
      switch (status) {
        case 'Paid':
          bg = 'bg-success';
          break;
        case 'Unpaid':
          bg = 'bg-warning text-dark';
          break;
        case 'Partially Refunded':
          bg = 'bg-info text-dark';
          break;
        case 'Refunded':
          bg = 'bg-secondary';
          break;
      }
      return bg;
    },
    paymentStatusLabel(status) {
      return {
        paid: 'Paid', unpaid: 'Unpaid', partial: 'Partial', refunded: 'Refunded',
      }[status] || status;
    },
    goBill() {
      this.$router.push({ name: 'bill-management' });
    },
    viewBill(bill) {
      console.log('View bill:', bill.id);
    },
    printReport() {
      this.printedAt = new Date().toLocaleString('th-TH', {
        dateStyle: 'medium', timeStyle: 'short',
      });
      this.$nextTick(() => window.print());
    },
  },
};
</script>

<style scoped>
.dashboard-loading {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity 0.2s;
}
.dashboard-loading-overlay {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1050;
}
</style>

<!-- Print styles — must be unscoped to reach layout elements (sidebar/navbar) -->
<style>
@media print {
  @page { size: A4; margin: 12mm; }

  body { background: #fff !important; }

  /* Hide layout chrome */
  .sidebar,
  .sidebar-overlay,
  .navbar,
  .wrapper > aside,
  .wrapper > nav { display: none !important; }

  /* Flatten main content area */
  .wrapper,
  .main,
  .content,
  .container-fluid { margin: 0 !important; padding: 0 !important; width: 100% !important; max-width: 100% !important; }

  /* Page title from route meta */
  .content > .container-fluid > .mb-3 > h3 { display: none !important; }

  /* Outer card frame from AdminLayout */
  .content .card.border-0.shadow-sm { box-shadow: none !important; border: none !important; }

  /* Card colors print-friendly */
  .card { break-inside: avoid; box-shadow: none !important; border: 1px solid #e5e7eb !important; }
  .card-header { background: #fff !important; }

  /* Loading state shouldn't show on print */
  .dashboard-loading { opacity: 1 !important; }
  .dashboard-loading-overlay { display: none !important; }

  /* Make charts fit one page width */
  .apexcharts-toolbar { display: none !important; }
}
</style>
